import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Check, Scale, Info, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';

/**
 * LegalDisclaimer - Notice juridique obligatoire
 * S'affiche au premier trade et accessible via bouton "?"
 */
const LegalDisclaimer = ({ 
  isOpen, 
  onClose, 
  onAccept,
  forceShow = false,  // Pour le bouton "?" - affiche même si déjà accepté
  language = 'fr'
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Reset scroll state when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    if (!forceShow) {
      localStorage.setItem('legal_disclaimer_accepted', 'true');
      localStorage.setItem('legal_disclaimer_date', new Date().toISOString());
    }
    onAccept?.();
    onClose();
  };

  const content = {
    fr: {
      title: "Avertissement Important",
      subtitle: "Veuillez lire attentivement avant de continuer",
      sections: [
        {
          icon: AlertTriangle,
          title: "Aucun Conseil en Investissement",
          text: "TRADEX AI est un outil d'aide à la décision uniquement informatif. Les analyses, signaux et recommandations fournis par cette application ne constituent EN AUCUN CAS des conseils en investissement, des recommandations d'achat ou de vente, ni des incitations à effectuer des transactions financières."
        },
        {
          icon: Scale,
          title: "Risques du Trading",
          text: "Le trading de crypto-monnaies, forex, actions et autres instruments financiers comporte des RISQUES ÉLEVÉS de perte en capital. Vous pouvez perdre la TOTALITÉ de votre investissement. N'investissez jamais plus que ce que vous pouvez vous permettre de perdre."
        },
        {
          icon: Info,
          title: "Performances Passées",
          text: "Les performances passées ne préjugent pas des performances futures. Les résultats historiques affichés dans cette application ne garantissent en aucun cas des résultats similaires à l'avenir."
        }
      ],
      footer: "En utilisant cette application, vous reconnaissez avoir lu et compris cet avertissement. Vous acceptez que toutes les décisions d'investissement sont prises sous votre seule responsabilité.",
      acceptButton: "J'ai lu et j'accepte",
      closeButton: "Fermer",
      scrollHint: "Faites défiler pour lire l'intégralité"
    },
    en: {
      title: "Important Disclaimer",
      subtitle: "Please read carefully before continuing",
      sections: [
        {
          icon: AlertTriangle,
          title: "Not Investment Advice",
          text: "TRADEX AI is an informational decision-support tool only. The analyses, signals, and recommendations provided by this application do NOT constitute investment advice, buy or sell recommendations, or incentives to execute financial transactions."
        },
        {
          icon: Scale,
          title: "Trading Risks",
          text: "Trading cryptocurrencies, forex, stocks, and other financial instruments carries HIGH RISKS of capital loss. You may lose your ENTIRE investment. Never invest more than you can afford to lose."
        },
        {
          icon: Info,
          title: "Past Performance",
          text: "Past performance is not indicative of future results. Historical results displayed in this application do not guarantee similar results in the future."
        }
      ],
      footer: "By using this application, you acknowledge that you have read and understood this disclaimer. You accept that all investment decisions are made at your own risk.",
      acceptButton: "I have read and accept",
      closeButton: "Close",
      scrollHint: "Scroll to read the full disclaimer"
    }
  };

  const t = content[language] || content.fr;

  if (!isOpen) return null;

  return (
    <>
      {/* Full screen overlay */}
      <div 
        className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Centered modal container */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Modal */}
        <div 
          className="relative w-full max-w-lg bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header with warning color */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-500/30 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{t.title}</h2>
                <p className="text-amber-200/70 text-sm mt-1">{t.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Scrollable content */}
          <div 
            className="p-6 max-h-[50vh] overflow-y-auto"
            onScroll={handleScroll}
          >
            <div className="space-y-5">
              {t.sections.map((section, index) => {
                const IconComponent = section.icon;
                return (
                  <div key={index} className="flex gap-4">
                    <div className="shrink-0 w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{section.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{section.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer text */}
            <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
              <p className="text-gray-300 text-sm leading-relaxed">{t.footer}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="p-6 border-t border-white/10 bg-black/30">
            {!hasScrolledToBottom && !forceShow && (
              <p className="text-center text-amber-400/70 text-xs mb-3 animate-pulse">
                {t.scrollHint}
              </p>
            )}
            <div className="flex gap-3">
              {forceShow ? (
                <Button
                  onClick={onClose}
                  className="w-full bg-white/10 hover:bg-white/20 text-white"
                >
                  {t.closeButton}
                </Button>
              ) : (
                <Button
                  onClick={handleAccept}
                  disabled={!hasScrolledToBottom}
                  className={`w-full transition-all ${
                    hasScrolledToBottom 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white' 
                      : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t.acceptButton}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Bouton "?" pour accéder au disclaimer
 */
export const DisclaimerHelpButton = ({ language = 'fr', className = '' }) => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowDisclaimer(true)}
        data-testid="disclaimer-help-button"
        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all
          bg-amber-500/20 border-2 border-amber-500/50 text-amber-500
          hover:bg-amber-500/30 hover:border-amber-500 
          dark:text-amber-400 dark:border-amber-500/50 dark:bg-amber-500/20
          ${className}`}
        title={language === 'fr' ? 'Avertissement légal' : 'Legal disclaimer'}
      >
        <HelpCircle className="w-5 h-5" />
      </button>
      
      <LegalDisclaimer
        isOpen={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
        forceShow={true}
        language={language}
      />
    </>
  );
};

/**
 * Composant d'avertissement à intégrer dans une page (non modal)
 */
export const DisclaimerBanner = ({ language = 'fr', className = '' }) => {
  const content = {
    fr: {
      title: "Avertissement Important",
      items: [
        "TRADEX AI est un outil d'aide à la décision uniquement informatif et ne constitue en aucun cas un conseil en investissement.",
        "Le trading comporte des risques élevés de perte en capital. N'investissez jamais plus que ce que vous pouvez vous permettre de perdre.",
        "Les performances passées ne préjugent pas des performances futures."
      ]
    },
    en: {
      title: "Important Disclaimer",
      items: [
        "TRADEX AI is an informational decision-support tool only and does not constitute investment advice.",
        "Trading carries high risks of capital loss. Never invest more than you can afford to lose.",
        "Past performance is not indicative of future results."
      ]
    }
  };

  const t = content[language] || content.fr;

  return (
    <div className={`p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 ${className}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-amber-400 mb-2">{t.title}</h3>
          <ul className="space-y-1">
            {t.items.map((item, index) => (
              <li key={index} className="text-sm text-gray-300 flex items-start gap-2">
                <span className="text-amber-400 mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook pour vérifier si le disclaimer a été accepté
 * S'affiche automatiquement si pas encore accepté
 */
export const useDisclaimerCheck = () => {
  const [needsDisclaimer, setNeedsDisclaimer] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  // Vérifier automatiquement au montage
  useEffect(() => {
    const accepted = localStorage.getItem('legal_disclaimer_accepted');
    if (!accepted) {
      setNeedsDisclaimer(true);
      setShowDisclaimer(true);
    }
  }, []);

  const checkDisclaimer = () => {
    const accepted = localStorage.getItem('legal_disclaimer_accepted');
    if (!accepted) {
      setNeedsDisclaimer(true);
      setShowDisclaimer(true);
      return false;
    }
    return true;
  };

  const handleAccept = () => {
    setNeedsDisclaimer(false);
    setShowDisclaimer(false);
  };

  return {
    needsDisclaimer,
    showDisclaimer,
    setShowDisclaimer,
    checkDisclaimer,
    handleAccept
  };
};

export default LegalDisclaimer;
